/**
 * Pearson's chi-square tests: independence in an r × c table, and goodness of fit.
 *
 * Matches `scipy.stats.chi2_contingency` and `scipy.stats.chisquare` (and R's
 * `chisq.test`). Yates's continuity correction is offered for 2 × 2 tables only, where
 * it is defined, and — as in both packages — never subtracts more than |O − E|.
 *
 * Effect sizes: Cramér's V = √(χ² / (N(min(r, c) − 1))) for any table, φ = √(χ² / N)
 * for a 2 × 2 table (where V and φ coincide), and Cohen's w = √(χ² / N) for goodness of
 * fit. All are computed from the uncorrected χ².
 */

import { chiSquareUpper } from "@/lib/stats/distributions";

export type ChiSquareFailure =
  | "too-small"
  | "negative"
  | "empty-margin"
  | "length-mismatch"
  | "bad-expected";

export interface IndependenceResult {
  observed: number[][];
  expected: number[][];
  rowTotals: number[];
  colTotals: number[];
  n: number;
  chiSquare: number;
  df: number;
  p: number;
  /** Yates-corrected χ² and p, for 2 × 2 tables only. */
  yates: { chiSquare: number; p: number } | null;
  cramersV: number;
  /** √(χ²/N), reported for 2 × 2 tables. */
  phi: number | null;
  /** (O − E)² / E for every cell. */
  contributions: number[][];
  /** (O − E) / √(E(1 − row/N)(1 − col/N)) — Haberman's adjusted residuals. */
  adjustedResiduals: number[][];
  minExpected: number;
  cellsBelowFive: number;
}

export type IndependenceOutcome =
  | { ok: true; result: IndependenceResult }
  | { ok: false; reason: ChiSquareFailure };

function totals(table: number[][]): { rows: number[]; cols: number[] } {
  const rows = table.map((row) => row.reduce((acc, value) => acc + value, 0));
  const cols = table[0].map((_, j) =>
    table.reduce((acc, row) => acc + row[j], 0)
  );
  return { rows, cols };
}

function yatesStatistic(observed: number[][], expected: number[][]): number {
  let total = 0;
  for (const [i, row] of observed.entries()) {
    for (const [j, value] of row.entries()) {
      const gap = Math.max(0, Math.abs(value - expected[i][j]) - 0.5);
      total += (gap * gap) / expected[i][j];
    }
  }
  return total;
}

export function chiSquareIndependence(
  observed: number[][]
): IndependenceOutcome {
  if (observed.length < 2 || (observed[0]?.length ?? 0) < 2) {
    return { ok: false, reason: "too-small" };
  }
  if (observed.some((row) => row.some((value) => value < 0))) {
    return { ok: false, reason: "negative" };
  }
  const { rows, cols } = totals(observed);
  const n = rows.reduce((acc, value) => acc + value, 0);
  if (rows.includes(0) || cols.includes(0)) {
    return { ok: false, reason: "empty-margin" };
  }
  const expected = rows.map((r) => cols.map((c) => (r * c) / n));
  const contributions = observed.map((row, i) =>
    row.map((value, j) => (value - expected[i][j]) ** 2 / expected[i][j])
  );
  const adjustedResiduals = observed.map((row, i) =>
    row.map(
      (value, j) =>
        (value - expected[i][j]) /
        Math.sqrt(expected[i][j] * (1 - rows[i] / n) * (1 - cols[j] / n))
    )
  );
  const chiSquare = contributions.flat().reduce((acc, value) => acc + value, 0);
  const df = (rows.length - 1) * (cols.length - 1);
  const is2x2 = rows.length === 2 && cols.length === 2;
  const yatesValue = is2x2 ? yatesStatistic(observed, expected) : null;
  const flatExpected = expected.flat();
  return {
    ok: true,
    result: {
      observed,
      expected,
      rowTotals: rows,
      colTotals: cols,
      n,
      chiSquare,
      df,
      p: chiSquareUpper(chiSquare, df),
      yates:
        yatesValue === null
          ? null
          : { chiSquare: yatesValue, p: chiSquareUpper(yatesValue, df) },
      cramersV: Math.sqrt(
        chiSquare / (n * (Math.min(rows.length, cols.length) - 1))
      ),
      phi: is2x2 ? Math.sqrt(chiSquare / n) : null,
      contributions,
      adjustedResiduals,
      minExpected: Math.min(...flatExpected),
      cellsBelowFive: flatExpected.filter((value) => value < 5).length,
    },
  };
}

export interface GoodnessOfFitResult {
  observed: number[];
  expected: number[];
  /** The expected proportions after normalising the weights to sum to 1. */
  proportions: number[];
  n: number;
  chiSquare: number;
  df: number;
  p: number;
  cohensW: number;
  contributions: number[];
  minExpected: number;
  cellsBelowFive: number;
}

export type GoodnessOfFitOutcome =
  | { ok: true; result: GoodnessOfFitResult }
  | { ok: false; reason: ChiSquareFailure };

/**
 * Goodness of fit against expected proportions or counts.
 *
 * `weights` may be proportions, percentages or expected counts — only their ratios
 * matter, so they are rescaled to the observed total. `null` means equal proportions.
 */
export function chiSquareGoodnessOfFit(
  observed: number[],
  weights: number[] | null
): GoodnessOfFitOutcome {
  const k = observed.length;
  if (k < 2) {
    return { ok: false, reason: "too-small" };
  }
  if (observed.some((value) => value < 0)) {
    return { ok: false, reason: "negative" };
  }
  const n = observed.reduce((acc, value) => acc + value, 0);
  if (n === 0) {
    return { ok: false, reason: "empty-margin" };
  }
  const raw = weights ?? observed.map(() => 1);
  if (raw.length !== k) {
    return { ok: false, reason: "length-mismatch" };
  }
  if (raw.some((value) => !(value > 0))) {
    return { ok: false, reason: "bad-expected" };
  }
  const weightTotal = raw.reduce((acc, value) => acc + value, 0);
  const proportions = raw.map((value) => value / weightTotal);
  const expected = proportions.map((value) => value * n);
  const contributions = observed.map(
    (value, i) => (value - expected[i]) ** 2 / expected[i]
  );
  const chiSquare = contributions.reduce((acc, value) => acc + value, 0);
  const df = k - 1;
  return {
    ok: true,
    result: {
      observed,
      expected,
      proportions,
      n,
      chiSquare,
      df,
      p: chiSquareUpper(chiSquare, df),
      cohensW: Math.sqrt(chiSquare / n),
      contributions,
      minExpected: Math.min(...expected),
      cellsBelowFive: expected.filter((value) => value < 5).length,
    },
  };
}
